// src/portal/portalMarkupLabels.js
//
// A FINDING'S NOTE, ON THE MAP. Bluebeam's text box, in permit-review terms:
// the reviewer places a point where they want text, and the note is the text.
//
// ── WHY NOT A SYMBOL LAYER ──────────────────────────────────────────────────
//
// MapLibre cannot render `text-field` without a `glyphs` endpoint, and
// ESRI_NEUTRAL_STYLE declares none — deliberately: its own header says "No
// token, no tile session, no entitlement, which is what lets a third-party
// submitter render a map at all". The only glyphs source in this tree is
// MapView.jsx:427, Mapbox's, behind an access token.
//
// AND THE FAILURE WOULD BE SILENT. Measured with the style spec's own
// validator: a symbol layer with no glyphs raises NOTHING. It validates clean,
// compiles clean, and renders no text — the unpainted-layer shape this feature
// has already hit twice (§3.22's collision, §3.28's empty _dblclick).
//
// So labels are DOM elements positioned by maplibre Markers. No glyphs, no font
// stack, no token; the text is selectable and reachable by a screen reader, and
// there is no configuration under which it renders nothing without saying so.
//
// ── WHY NOT IN portalMapStyle.js ────────────────────────────────────────────
//
// The plan put this beside addMarkupLayers. portalMapStyle.test.js runs
// `@vitest-environment node` with NO maplibre mock, so importing maplibre-gl
// into that module would pull a browser library into a node test and break its
// eighteen assertions. This module owns the runtime dependency; portalMapStyle
// stays pure style data.
//
// ── OVERLAPPING LABELS OVERLAP, VISIBLY ─────────────────────────────────────
//
// `text-allow-overlap` defaults to false, so a symbol layer would silently hide
// colliding labels: THE MAP SHOWING LESS THAN EXISTS, which is the count-badge
// problem again. Markers do not collide and nothing is hidden. That trades
// silent loss for visible clutter, and visible clutter is the right trade — a
// reviewer who sees two labels overlap knows there are two; one shown with the
// other suppressed has no way to learn it. If clutter becomes the problem, that
// is a finding from use rather than a guess now.
//
// ── NOTHING HERE TOUCHES SELECTION ──────────────────────────────────────────
//
// No map event is bound, and the label elements carry `pointer-events: none`,
// so a click passes through to the map exactly as it did before. That is not
// incidental: placement lands in the next commit and widens `isSuspended`, and
// it should inherit a clean click surface rather than an interaction already
// half-broken. Asserted in portalMarkupLabels.test.jsx.

import maplibregl from "maplibre-gl";

import { findingNumber } from "./portalFindingNumber";

/** Where a label cuts. See labelTextFor. */
export const LABEL_MAX_CHARS = 48;

/**
 * The text a label shows for a note.
 *
 * ── THE LABEL IS A POINTER, NOT A REPLACEMENT ───────────────────────────────
 *
 * A note has a floor (MARKUP_NOTE_MIN, 10) and NO CEILING — `collab_events`-style
 * unbounded text. A map label that rendered all of it would be a paragraph on a
 * pad. The full note stays in the findings list, which is the authority; this is
 * a pointer to it.
 *
 * Cuts at the last word boundary at or before LABEL_MAX_CHARS, so a label never
 * ends mid-word, and hard-cuts a single token longer than that. The ellipsis is
 * the visible signal that there is more; the element's `title` carries the whole
 * note for a hover, and the list carries it for everyone else.
 *
 * 48 is chosen to CUT rather than to avoid cutting: the three notes in the
 * database are 42, 58 and 65 characters, so two of three are truncated. A
 * threshold tuned above the longest real note would be untested by construction.
 */
export function labelTextFor(note) {
  const text = typeof note === "string" ? note.trim() : "";
  if (text.length <= LABEL_MAX_CHARS) return text;
  const cut = text.slice(0, LABEL_MAX_CHARS);
  const lastSpace = cut.lastIndexOf(" ");
  // A single token longer than the limit has no boundary to cut on.
  const body = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  return `${body.replace(/[.,;:]$/, "")}…`;
}

/** Only a Point finding labels. §3.31 has the argument. */
export function labellableMarkup(markup = []) {
  return (markup ?? []).filter(
    (m) => m?.geometry?.type === "Point"
      && Array.isArray(m.geometry.coordinates)
      && typeof labelTextFor(m.note) === "string"
      && labelTextFor(m.note).length > 0
  );
}

function buildLabelElement(markup) {
  const el = document.createElement("div");
  el.dataset.testid = `markup-label-${markup.id}`;
  el.className = "portal-markup-label";
  // ── THE NUMBER AND THE NOTE ARE SEPARATE CHILDREN ───────────────────────
  //
  // The mock's numbered pin and the note text are one label here, because two
  // markers at one coordinate collide with each other. They are separate
  // ELEMENTS so that each can be read on its own: the number is what a reviewer
  // says out loud ("look at three"), the note is what they read.
  //
  // THE NUMBER COMES FROM findingNumber, the same call the list makes. That is
  // the whole of "pin n on the map is finding n in the list" — one function,
  // two renderers, and a test that compares the two rendered artifacts.
  const n = findingNumber(markup);
  if (n !== null) {
    const badge = document.createElement("span");
    badge.dataset.testid = `markup-pin-${markup.id}`;
    badge.className = "portal-markup-pin";
    badge.textContent = String(n);
    // Styled inline for the same reason the label is: these elements are
    // created outside React and mounted by a MapLibre Marker, so no stylesheet
    // rule is guaranteed to have reached them.
    //
    // THE PIN TAKES THE MARKUP COLOUR, not a kind colour. --geom-markup is
    // already what a reviewer's annotation is drawn in on this map, so a pin
    // reads as "a finding" rather than as a sixth category. The kind colours
    // were measured against the geometry palette and one of them collides:
    // surface_conflict sits at CIEDE2000 3.67 from a road. See
    // PortalFindingsPanel.jsx for the full measurement.
    //
    // ── text-shadow:none IS THE FIX, AND IT IS NOT COSMETIC ────────────────
    //
    // The label sets a dark halo -- "0 0 2px #0C0E10, 0 0 3px #0C0E10" -- and
    // it is what makes WHITE text readable over an arbitrary aerial photograph.
    // text-shadow INHERITS. So the near-black digit inside this pin was drawn
    // with a near-black blur around it, which filled its counters and smeared
    // the stroke. Rendered side by side, the smudge is the whole of why the
    // number was hard to read: the contrast is 7.30:1, above AAA, and was never
    // the problem.
    //
    // The pin does not need a halo. It sits on its own opaque fill, which is
    // the same job the halo does for the text.
    //
    // 18px/11px/700 over 14px/9px/600 because the digit now matches the label's
    // own 11px rather than being a shrunken version of it -- compared by
    // rendering four sizes at 3x over a caliche-toned backdrop.
    badge.style.cssText = [
      "display:inline-flex", "align-items:center", "justify-content:center",
      "width:18px", "height:18px", "margin-right:4px",
      "border-radius:50%", "background:var(--geom-markup-rgb, #f472b6)",
      "color:#0C0E10", "font-size:11px", "font-weight:700",
      "font-family:var(--font-mono, ui-monospace, monospace)",
      "text-shadow:none", "flex-shrink:0",  
    ].join(";");
    el.appendChild(badge);
  }
  const body = document.createElement("span");
  body.dataset.testid = `markup-note-${markup.id}`;
  body.textContent = labelTextFor(markup.note);
  el.appendChild(body);
  // The whole note, for a hover. The list is still where it is read properly.
  el.title = typeof markup.note === "string" ? markup.note : "";
  // ── THE CLICK PASSES THROUGH ────────────────────────────────────────────
  // A label that swallowed clicks would be a fifth participant on the map's
  // click, next to MlDraw, three layer handlers and the deselect. It is not.
  el.style.pointerEvents = "none";
  // ── THE HALO IS WHAT MAKES IT LEGIBLE, NOT THE COLOUR ───────────────────
  // Both portal maps render Esri World Imagery; #0C0E10 is only the pre-load
  // canvas. Over aerial tiles there is no single backdrop to measure against —
  // bare white text is 11.40:1 on asphalt and 1.29:1 on bright caliche, which
  // is exactly what a pad IS. Read against its own halo, white on #0C0E10 is
  // 19.34:1 regardless of the tile underneath.
  // ── THE HALO IS NOT A THEME TOKEN, AND THAT IS ON PURPOSE ────────────────
  //
  // #0C0E10 here is a LEGIBILITY DEVICE, not a surface. White-on-imagery is
  // 11.40:1 on asphalt and 1.29:1 on bright caliche, and a pad IS caliche; the
  // chip and the halo are what make the 19.34:1 above true regardless of the
  // tile underneath. Bind that to a themeable variable and a palette change
  // silently invalidates a measured contrast claim, in the one place on this
  // map where text has to be readable over an arbitrary photograph.
  //
  // It is the same value as --map-backdrop and it is deliberately NOT that
  // token: two meanings sharing one number is how §3.22's collision happened.
  // The canvas pre-load background IS a surface and does use the token.
  //
  // It is also the value jsdom refuses to parse inside `rgb(var(…) / …)`,
  // which is how this was found: the token form dropped the background
  // entirely and the assertion below caught it.
  el.style.cssText += [
    "color:#ffffff",
    "font-size:11px",
    "line-height:1.25",
    "white-space:nowrap",
    "display:inline-flex",
    "align-items:center",
    "padding:2px 5px",
    "border-radius:3px",
    "background:rgba(12,14,16,0.72)",
    "text-shadow:0 0 2px #0C0E10, 0 0 3px #0C0E10",
    "pointer-events:none",
    "user-select:none",
  ].join(";");
  return el;
}

// One marker registry per map. WeakMap so a removed map is collectable and two
// maps on one page (there are never two today, but the module cannot know that)
// do not share state.
const registries = new WeakMap();

/**
 * Put a label on every Point finding, and take away the ones that have gone.
 *
 * IDEMPOTENT, like addSubmissionLayers and addMarkupLayers: safe to call again
 * when markup changes. Both portal maps call it, from both their load handler
 * and their redraw effect — markup arriving AFTER load is the re-read-after-
 * return path, and §3.30's M7 was exactly that case going untested.
 */
export function syncMarkupLabels(map, markup = []) {
  if (!map) return;
  let live = registries.get(map);
  if (!live) { live = new Map(); registries.set(map, live); }

  const wanted = new Map(labellableMarkup(markup).map((m) => [m.id, m]));

  for (const [id, marker] of [...live]) {
    if (wanted.has(id)) continue;
    marker.remove();
    live.delete(id);
  }

  for (const [id, m] of wanted) {
    const existing = live.get(id);
    if (existing) {
      // Same finding, possibly moved or re-noted. Markup is immutable once
      // frozen, so this is the unfrozen-reviewer case.
      existing.setLngLat(m.geometry.coordinates);
      const el = existing.getElement?.();
      if (el) { el.textContent = labelTextFor(m.note); el.title = m.note ?? ""; }
      continue;
    }
    const marker = new maplibregl.Marker({
      element: buildLabelElement(m),
      // Beside the point, not over it: the circle the markup point layer draws
      // is the anchor, and a label centred on it would hide it.
      anchor: "left",
      offset: [10, 0],
    })
      .setLngLat(m.geometry.coordinates)
      .addTo(map);
    live.set(id, marker);
  }
}

/** Every label currently on this map. Exported for the tests to inspect. */
export function labelsOn(map) {
  return registries.get(map) ?? new Map();
}
